import React, { useState, useEffect, useRef } from "react";
import { Button, Row, Col, FormControl, Container } from "react-bootstrap";
import { Loader, Icon } from "semantic-ui-react";
import { useNavigate } from "react-router-dom";

import "./manage-course-container.css";
import OraclelearnRosterTableV2 from "../components/oracleLearnRosterTableV2";

const ManageCourseContainer = ({
  courseDetails,
  loadingState: { isLoading, setIsLoading, enrollmentsLoading },
  search: { term, onChangeTerm, onFindLearner, onConfirm },
}) => {
  const [scrollStartOrEnd, setScrollStartOrEnd] = useState(null);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);

  const inputElement = useRef();
  const scrollRef = useRef();

  const navigate = useNavigate();

  const handleNav = (direction) => {
    if (!scrollRef?.current) return;

    // this calculates current scroll position and finds the remaining amount needed to scroll to the end (or beginning, it depends on which button is clicked)
    const remainingScrollWidth = scrollRef.current.scrollWidth - scrollRef.current.scrollLeft;

    scrollRef.current.scrollBy({
      left: direction === "left" ? -remainingScrollWidth : remainingScrollWidth,
      behavior: "smooth",
    });
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    if (scrollWidth <= clientWidth) {
      setScrollStartOrEnd("no-scroll"); // new state for no scrolling needed
    } else if (scrollLeft === 0) {
      setScrollStartOrEnd("start");
    } else if (scrollLeft + clientWidth >= scrollWidth - 1) {
      // -1 for rounding tolerance
      setScrollStartOrEnd("end");
    } else {
      setScrollStartOrEnd(null);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    navigate("/selectRoster", { replace: true });
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Call on mount
    handleScroll();

    // Event listeners
    container.addEventListener("scroll", handleScroll);

    const handleResize = () => {
      handleScroll(); // Re-check scroll position on resize
    };

    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const courseDetailsHeaders = courseDetails.map((courseDetail, index) => (
    // only add the vertical line between columns if the current element is not the last one.
    <div
      key={index}
      className={`${index !== courseDetails.length - 1 ? "manage-course-info" : ""} px-4`}
      xs="auto"
    >
      <h4 className="course-details-title font-weight-bold mb-0">{courseDetail.key}</h4>
      <div className="course-details-content">{courseDetail.value}</div>
    </div>
  ));

  return (
    <Container className="bg-white rounded py-3 position-relative manage-course">
      {isLoading && (
        <div className="spinner-overlay-container">
          <div className="spinner-overlay">
            <Loader
              active
              size="big"
              animation="border"
              variant="light"
              inline="centered"
              inverted
            />
            <div className="spinner-text text-light">One Moment...</div>
          </div>
        </div>
      )}

      <Row>
        <Col>
          <h3 className="font-weight-bold">Manage Roster - Scheduled Training</h3>
        </Col>
        <Col className="d-flex justify-content-end" xs={2}>
          <Button variant="secondary" onClick={handleBack}>
            Go Back
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr />
        </Col>
      </Row>

      <Row className="manage-course-info-container mb-3">
        {scrollStartOrEnd !== "start" && scrollStartOrEnd !== "no-scroll" && (
          <Button className="arrow-btn left" variant="none" onClick={() => handleNav("left")}>
            <Icon name="chevron left" />
          </Button>
        )}
        <div ref={scrollRef} className="manage-course-info-container d-flex">
          {courseDetailsHeaders}
        </div>
        {scrollStartOrEnd !== "end" && scrollStartOrEnd !== "no-scroll" && (
          <Button className="arrow-btn right" variant="none" onClick={() => handleNav("right")}>
            <Icon name="chevron right" />
          </Button>
        )}
      </Row>

      <Row>
        <Col xs={3}>
          <section className="manage-course-learner-search mt-4 px-3 py-2">
            <header className="d-flex align-items-center mb-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-person-fill-add"
                viewBox="0 0 16 16"
              >
                <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.5-5v1h1a.5.5 0 0 1 0 1h-1v1a.5.5 0 0 1-1 0v-1h-1a.5.5 0 0 1 0-1h1v-1a.5.5 0 0 1 1 0m-2-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
                <path d="M2 13c0 1 1 1 1 1h5.256A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1 1.544-3.393Q8.844 9.002 8 9c-5 0-6 3-6 4" />
              </svg>
              <h3 className="manage-course-learner-title ml-2">Learner Check-In</h3>
            </header>
            <p className="manage-course-learner-desc mb-0">
              Scan Badge or Enter ID <br />
              (ID=CDSID, Global ID, or Badge ID)
            </p>

            <FormControl
              onChange={onChangeTerm}
              ref={inputElement}
              value={term}
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
              onKeyPress={(event) => {
                if (event.key === "Enter") {
                  onConfirm(event);
                }
              }}
            />
            <div className="d-flex">
              <Button onClick={onConfirm} className="mx-auto mt-3 mb-3 px-4 btn standard-btn bold">
                CONFIRM
              </Button>
            </div>
            <div className="border-top mx-2" />
            <Button variant="none" onClick={() => setAdvancedSearchOpen(!advancedSearchOpen)}>
              <span className="triangle-pointer-container">
                <Icon
                  name="triangle right"
                  size="large"
                  className={`triangle-pointer ${advancedSearchOpen ? "down" : "right"}`}
                />
              </span>
              {` `}
              Advanced
            </Button>
            {advancedSearchOpen && (
              <div className="mt-2 mb-2">
                <Button
                  onClick={(e) => {
                    onFindLearner(e);
                  }}
                  className="d-flex rounded-pill btn standard-btn bold"
                >
                  <Icon name="search" />
                  <div>Find Learner</div>
                </Button>
              </div>
            )}
          </section>
        </Col>

        <OraclelearnRosterTableV2
          setIsLoading={setIsLoading}
          enrollmentsLoading={enrollmentsLoading}
        />
      </Row>
    </Container>
  );
};

export default ManageCourseContainer;
